import { ProductFormContent } from '@/components/sheet-contents/NewProductContent'
import { TestLabelButton } from '@/components/dashboard/TestLabelButton'
import { TestListingNotice } from '@/components/TestListingNotice'
import { getATagFromCoords } from '@/lib/utils/coords'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { resolveProductWorkflow } from '@/lib/workflow/productWorkflowResolver'
import { authStore } from '@/lib/stores/auth'
import { productFormActions } from '@/lib/stores/product'
import { hasProductFormDraft } from '@/lib/utils/productFormStorage'
import { getProductId, productsByPubkeyQueryOptions } from '@/queries/products'
import { useDashboardTitle } from '@/routes/_dashboard-layout'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { useCallback, useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/_dashboard-layout/dashboard/products/products/$productId')({
	component: EditProductComponent,
})

type InitState = 'idle' | 'checking' | 'loading-draft' | 'loading-product' | 'ready'

function EditProductComponent() {
	const { productId } = Route.useParams()
	const { user } = useStore(authStore)
	const [initState, setInitState] = useState<InitState>('idle')

	// Simple lock to prevent concurrent initialization
	const lockRef = useRef(false)

	useDashboardTitle('Edit Product')

	// Fetch user's products to find the one being edited (including hidden products)
	const editProductOptions = productsByPubkeyQueryOptions(user?.pubkey ?? '', true)
	const { data: products = [], isLoading: isLoadingProducts } = useQuery({
		...editProductOptions,
		enabled: editProductOptions.enabled,
	})

	// Find the product being edited (productId is the event.id from the URL)
	const product = products.find((p) => p.id === productId)
	const productExists = !!product

	// Get the d tag value - this is what we use for draft storage (consistent with editingProductId)
	const productDTag = product ? getProductId(product) : null
	const workflow = resolveProductWorkflow({
		mode: 'edit',
		editingProductId: productDTag,
		v4vConfigurationState: 'unknown',
	})

	const initializeForm = useCallback(async () => {
		if (!productDTag) return
		if (lockRef.current) return
		lockRef.current = true

		try {
			setInitState('checking')
			productFormActions.reset({
				activeTab: workflow.initialTab,
				editingProductId: productDTag,
			})

			// Use productDTag for draft lookup (same key used for saving)
			const hasDraft = await hasProductFormDraft(productDTag)

			if (hasDraft) {
				// Auto-load the draft instead of prompting
				setInitState('loading-draft')
				await productFormActions.loadDraftForProduct(productDTag, {
					activeTab: workflow.initialTab,
				})
				setInitState('ready')
			} else {
				setInitState('loading-product')
				await productFormActions.loadProductForEdit(productId, {
					preserveTabState: { activeTab: workflow.initialTab },
				})
				setInitState('ready')
			}
		} finally {
			lockRef.current = false
		}
	}, [productId, productDTag, workflow.initialTab])

	// Effect to reset state when productId changes
	useEffect(() => {
		setInitState('idle')
		lockRef.current = false
	}, [productId])

	// Reset the form when leaving this edit view, unless another session has already taken over
	useEffect(() => {
		if (!productDTag) return
		return () => productFormActions.endEditProductSession(productDTag)
	}, [productDTag])

	// Effect to start initialization when ready
	const canInitialize = productExists && productDTag !== null && initState === 'idle'
	useEffect(() => {
		if (canInitialize) {
			initializeForm()
		}
	}, [canInitialize, initializeForm])

	// Loading products from query
	if (isLoadingProducts) {
		return (
			<Card>
				<CardContent className="p-8">
					<div className="space-y-4">
						<Skeleton className="h-8 w-48" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
					</div>
				</CardContent>
			</Card>
		)
	}

	// Product not found
	if (!product) {
		return (
			<Card>
				<CardContent className="p-8 text-center">
					<h1 className="text-2xl font-bold">Product Not Found</h1>
					<p className="text-gray-500 mt-2">The product you're looking for doesn't exist or you don't have access to it.</p>
				</CardContent>
			</Card>
		)
	}

	// Checking for draft or loading product
	if (initState === 'idle' || initState === 'checking' || initState === 'loading-draft' || initState === 'loading-product') {
		return (
			<Card>
				<CardContent className="p-8">
					<div className="space-y-4">
						<Skeleton className="h-8 w-48" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
					</div>
				</CardContent>
			</Card>
		)
	}

	// Ready to show the form - pass productDTag for draft checking and productId for reloading
	return (
		<div className="space-y-4">
			{/* ADR-0009: authorized labelers can mark / unmark this item as a test listing */}
			{productDTag && (
				<div className="flex justify-end items-center gap-3">
					{/* ADR-0009: the owner sees the item in their dashboard even when it is
					    curated out of the public feed — say why. */}
					<TestListingNotice
						coordinate={getATagFromCoords({ kind: 30402, pubkey: product.pubkey, identifier: productDTag })}
						itemLabel="Product"
					/>
					<TestLabelButton kind={30402} pubkey={product.pubkey} dTag={productDTag} itemLabel="Product" />
				</div>
			)}
			<ProductFormContent showFooter={true} productDTag={productDTag} productEventId={productId} workflow={workflow} />
		</div>
	)
}
